import type { IMigrationStatus } from '@experiences/interfaces';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSWRConfig } from 'swr';

import { licenseManagementMigrationStatusUrl } from '../../../services/licensing/management/AccountService';
import {
    accountGlobalId as accountGlobalIdSelector,
    profile,
} from '../../../store/selectors';

/**
 * Revalidates the Flex→Uni migration status + progress SWR caches after an
 * allocation change, but only when the cached status already says migration is
 * active — otherwise the endpoints have nothing to report and calling them is
 * pointless traffic.
 */
export const useMigrationStateRevalidator = () => {
    const {
        mutate, cache,
    } = useSWRConfig();
    const profileState = useSelector(profile);
    const organizationId = useSelector(accountGlobalIdSelector);
    const { licenseCode } = profileState.accountUserDto;

    return useCallback(() => {
        if (!licenseCode) {
            return;
        }
        const statusKey = `${licenseManagementMigrationStatusUrl}/${licenseCode}`;
        const cachedStatus = cache.get(statusKey)?.data as IMigrationStatus | undefined;
        if (!cachedStatus?.enableMigration) {
            return;
        }
        void mutate(statusKey);
        if (organizationId) {
            void mutate(`la-migration-progress-${organizationId}`);
        }
    }, [ cache, licenseCode, mutate, organizationId ]);
};
